import { useAtomValue } from 'jotai';

import { Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { Document, DocumentKey } from '@ekhodealer/ekho-common';

import { EmptySection } from '@common/components/EmptySection';

import { useGetOrderDocuments } from '../../api/useGetOrderDocuments';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';

import { DocumentList } from './DocumentList';
import { DocumentsSkeleton } from './DocumentsSkeleton';

export const DocumentsTabContent = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const { data: orderDocuments, isLoading } = useGetOrderDocuments(orderId);

	// Show skeleton while loading
	if (isLoading) {
		return <DocumentsSkeleton />;
	}

	if (!orderDocuments) {
		return null;
	}

	const {
		buyerIdentificationDocuments,
		saleDocuments,
		financingInsuranceDocuments,
		titlingRegistrationDocuments,
	} = orderDocuments;

	// Helper to convert document objects to arrays with keys for rendering
	const getDocumentsWithKeys = (documentObj: Record<string, Document | undefined>) => {
		return Object.entries(documentObj)
			.filter((entry): entry is [DocumentKey, Document] => entry[1] !== undefined)
			.map(([key, document]) => ({ key: key as DocumentKey, document }));
	};

	// Helper to render a section only if it has documents
	const renderSection = (
		title: string,
		documentObj: Record<string, Document | undefined> | undefined,
	) => {
		if (!documentObj) {
			return null;
		}

		const documents = getDocumentsWithKeys(documentObj);
		if (documents.length === 0) {
			return null;
		}

		return (
			<Stack itemsSpacing='16'>
				<Text size='body' weight='semibold'>
					{title}
				</Text>
				<DocumentList documents={documents} orderId={orderId} />
			</Stack>
		);
	};

	const sections = [
		renderSection('Buyer identification documents', buyerIdentificationDocuments),
		renderSection('Sale documents', saleDocuments),
		renderSection('Financing & insurance contracts', financingInsuranceDocuments),
		renderSection('Titling and registration documents', titlingRegistrationDocuments),
	].filter(Boolean);

	return (
		<Stack itemsSpacing='24' padding={{ top: '12' }}>
			{sections.length > 0 ? (
				sections
			) : (
				<EmptySection message='No documents available at this time' />
			)}
		</Stack>
	);
};
